
import {
  defineComponent,
  PropType,
  computed,
  ref,
  onMounted,
  onUpdated,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { useStore } from '@/store';

import Tooltip from 'primevue/tooltip';

import SpmButton from '@/components/spm-primevue/SpmButton.vue';

import {
  FieldsGroup,
  TabItem,
  TabSubitem,
  ParserObject,
  SelectedStructureSyncPart,
  TemplateStructureEnum,
} from '@/types';
import StructureEdit from '@/components/template-builder/fields/StructureEdit.vue';
import ToggleMobileSettings from '@/components/template-builder/fields/partials/ToggleMobileSettings.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Divider from 'primevue/divider';
import { showElementsModal } from '@/composables/template-editor/TemplateEditor';

import ConfigurationPanelTabContentFieldGroup from '@/components/template-builder/panels/ConfigurationPanelTabContentFieldGroup.vue';
import { getTemplateIframeDocument } from '../utils/helpers';

export default defineComponent({
  name: 'ConfigurationPanelTabContentItem',

  components: {
    StructureEdit,
    TabView,
    TabPanel,
    ConfigurationPanelTabContentFieldGroup,
    Divider,
    ToggleMobileSettings,
    SpmButton,
    HelpMessage,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    tabItem: {
      type: Object as PropType<TabItem | TabSubitem>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserObject>,
      required: true,
    },

    fieldGroup: {
      type: Object as PropType<FieldsGroup | TabItem>,
      required: true,
    },

    fieldGroupIndex: {
      type: Number,
      required: true,
    },

    activeFieldGroupIndex: {
      type: Array as PropType<number[]>,
      required: true,
    },

    headerTitle: {
      type: String,
      required: false,
      default: '',
    },

    isSynchronizedPage: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['toggle-field-group', 'refresh'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();

    const showFieldGroup = computed(() => props.parserValues[props.tabItem.label].toggleItemsDisplay?.[props.fieldGroup.label]?.value ?? true);
    const selectedStructure = computed(() => store.getters['liveEditor/getSelectedStructure']);

    const componentInSyncPart = computed(() => {
      if ('elementPartType' in props.fieldGroup && props.fieldGroup.elementPartType && selectedStructure.value) {
        return selectedStructure.value.syncParts.find((syncPart: SelectedStructureSyncPart) => syncPart.partType === (props.fieldGroup as FieldsGroup).elementPartType);
      }
      return null;
    });

    const fieldGroupLabel = computed(() => {
      if (componentInSyncPart.value) {
        return `${t(props.fieldGroup.label)} : ${componentInSyncPart.value.partName}`;
      }
      return t(props.fieldGroup.label);
    });

    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const hideLoadElementBtn = computed(() => {
      if (selectedSyncElement.value && props.fieldGroup.elementPartType && selectedStructure.value) {
        return selectedStructure.value.syncParts.find((syncPart: SelectedStructureSyncPart) => syncPart.syncId === selectedSyncElement.value.syncElementId);
      }
      return false;
    });

    const canSaveStyle = computed(() => {
      if (!selectedSyncElement.value) {
        return true;
      }
      if (selectedSyncElement.value && selectedSyncElement.value.elementType === TemplateStructureEnum.SECTION && !selectedSyncElement.value.elementPartType) {
        return true;
      }
      return false;
    });

    const toggleFieldGroup = (index: number) => {
      emit('toggle-field-group', index);
    };

    const handleRefresh = () => {
      emit('refresh');
    };

    const handleChangeMobileSettings = (data: { tabItemLabel: string; fieldGroupLabel: string; value: boolean }) => {
      store.dispatch('liveEditor/toggleSetting', data);
    };

    const handleShowSaveElementModal = () => {
      store.commit('liveEditor/showSaveElementModal', {
        selector: selectedStructure.value ? `#${selectedStructure.value.identifier}` : '',
        type: TemplateStructureEnum.WIDGET,
        elementPartType: props.fieldGroup.elementPartType,
        fromSyncElement: !canSaveStyle.value,
      });
    };

    const handleShowElementModal = () => {
      if (selectedStructure.value) {
        const template = getTemplateIframeDocument();
        if (template) {
          const element = template.querySelector(`#${selectedStructure.value.identifier}`);
          const elementSubtype = element?.getAttribute('data-widgettype') || '';
          showElementsModal([selectedStructure.value.type], {
            elementPartType: props.fieldGroup.elementPartType || '',
            elementSubtype,
            fromSyncElement: !!selectedSyncElement.value,
          });
        }
      }
    };

    const enterEditSyncElement = () => {
      const elementType = componentInSyncPart.value ? selectedStructure.value.type : TemplateStructureEnum.DESIGN;
      const name = componentInSyncPart.value ? '' : props.headerTitle;

      store.dispatch('liveEditor/showSyncElementWarning', {
        elementId: selectedStructure.value.identifier,
        elementType,
        name,
        elementPartType: props.fieldGroup.elementPartType,
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        acceptCallback: () => {},
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        rejectCallback: () => {},
      });
    };

    // Ref for TabView to force scrollable arrows recalculation
    const tabViewRef = ref<any>(null);
    const updateTimeout = ref<any>(null);

    // Update button visibility based on scroll position
    const updateButtonVisibility = () => {
      if (!tabViewRef.value) return;

      const tabViewEl = tabViewRef.value.$el;
      if (!tabViewEl) return;

      const navContent = tabViewEl.querySelector('.p-tabview-nav-content') as HTMLElement | null;
      const prevBtn = tabViewEl.querySelector('.p-tabview-nav-prev') as HTMLElement | null;
      const nextBtn = tabViewEl.querySelector('.p-tabview-nav-next') as HTMLElement | null;

      if (navContent) {
        const { scrollLeft, scrollWidth, clientWidth } = navContent;
        const canScrollLeft = scrollLeft > 0;
        const canScrollRight = Math.ceil(scrollLeft + clientWidth) < scrollWidth;

        // Hide/show prev button based on scroll position
        if (prevBtn) {
          prevBtn.style.display = canScrollLeft ? 'flex' : 'none';
        }

        // Hide/show next button based on scroll position
        if (nextBtn) {
          nextBtn.style.display = canScrollRight ? 'flex' : 'none';
        }
      }
    };

    // Force TabView scrollable arrows recalculation (fixes PrimeVue bug)
    const updateTabViewScrollButtons = () => {
      nextTick(() => {
        updateTimeout.value = setTimeout(() => {
          if (tabViewRef.value) {
            // Try to call updateButtonState if available (PrimeVue internal method)
            if (typeof tabViewRef.value.updateButtonState === 'function') {
              tabViewRef.value.updateButtonState();
            }
            // Access the DOM element and trigger scroll to force button state update
            const tabViewEl = tabViewRef.value.$el;
            if (tabViewEl) {
              const navContent = tabViewEl.querySelector('.p-tabview-nav-content');
              if (navContent) {
                // Trigger a small scroll to force PrimeVue to recalculate
                navContent.scrollLeft = 1;
                navContent.scrollLeft = 0;

                // Add scroll event listener to update button visibility on scroll
                navContent.removeEventListener('scroll', updateButtonVisibility);
                navContent.addEventListener('scroll', updateButtonVisibility);
              }
            }
            // Also trigger resize event as fallback
            window.dispatchEvent(new Event('resize'));

            // Update button visibility based on current scroll position
            updateButtonVisibility();
          }
        }, 100);
      });
    };

    onMounted(() => {
      updateTabViewScrollButtons();
      setTimeout(updateTabViewScrollButtons, 300);
    });

    onUpdated(() => {
      updateTabViewScrollButtons();
    });

    onBeforeUnmount(() => {
      if (updateTimeout.value) {
        clearTimeout(updateTimeout.value);
      }
    });

    return {
      t,
      showFieldGroup,
      fieldGroupLabel,
      hideLoadElementBtn,
      componentInSyncPart,
      selectedSyncElement,
      canSaveStyle,
      toggleFieldGroup,
      handleRefresh,
      handleChangeMobileSettings,
      handleShowSaveElementModal,
      handleShowElementModal,
      enterEditSyncElement,
      tabViewRef,
    };
  },
});
